import React, { FC, useCallback, useEffect } from 'react';
import { useHttpClient, useTranslation } from '@wix/fe-essentials-standalone';
import {
  ACTIONS,
  useTemplateContext,
} from '@components/Contexts/TemplateContext';
import Reviews from '@components/Reviews';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import {
  MODAL_ACTIONS,
  useModalsContext,
} from '@components/Contexts/ModalsContext';
import AddReviewBanner from './AddReviewBanner';
import { Box } from '@wix/design-system';
import dynamic from 'next/dynamic';
import { ReviewData } from '@common/types';
import {
  TEMPLATE_DELETE_REVIEW_REPLY_API_URL,
  TEMPLATE_POST_REVIEW_REPLY_API_URL,
} from '@utils/constants';

const DynamicTemplateReviewModal = dynamic(
  () => import('@components/Reviews/ReviewModal/TemplateReviewModal'),
);

type TemplateReviewsProps = {
  isMobileView: boolean;
  showReviewsCount: boolean;
};

const translations = {
  viewReviewSubjectCTA: 'marketplace.templates-page.view-site-cta',
  replyAuthorTitle: 'marketplace.template-page.reply-title',
  noReviews: 'marketplace.templates-page.no-reviews-text',
};

const TemplateReviews: FC<TemplateReviewsProps> = ({
  isMobileView,
  showReviewsCount,
}) => {
  const httpClient = useHttpClient();
  const { t } = useTranslation();
  const { logBiClick } = useTemplateBi({ is_mobile: isMobileView });
  const {
    dispatch,
    template,
    reviewsInfo,
    loadNewReview,
    isEligibleToGiveReview,
    userAlreadyLeftReview,
    userReviewForTemplate,
  } = useTemplateContext();

  const { dispatchModal } = useModalsContext();

  useEffect(() => {
    const hasOpenModalParam = new URLSearchParams(window.location.search).has(
      'showModal',
    );
    if (hasOpenModalParam && userAlreadyLeftReview) {
      showModalError();
    } else if (hasOpenModalParam && showReviewBanner) {
      openReviewModal();
    }
  }, []);

  const showReviewBanner =
    !template.isOwnerView && isEligibleToGiveReview && !userAlreadyLeftReview;

  const showModalError = () => {
    dispatchModal({
      type: MODAL_ACTIONS.SHOW_ERROR_TOAST,
      message: t(
        'marketplace.template-page.review.error-user-already-reviewed',
      ),
    });
  };

  const openReviewModal = (openUsingBanner = false) => {
    if (openUsingBanner) {
      dispatch({
        type: ACTIONS.REVIEW_MODAL_AUTO_FOCUS,
        reviewModalAutoFocus: true,
      });
    }
    dispatchModal({
      type: MODAL_ACTIONS.SHOW_REVIEW_MODAL,
      showReviewModal: true,
    });
  };

  const onSubmitSuccess = (review: ReviewData) => {
    if (userAlreadyLeftReview) {
      dispatch({
        type: ACTIONS.DELETE_REVIEW,
        payload: review,
      });
    }
    dispatch({
      type: ACTIONS.LOAD_NEW_REVIEW,
      loadNewReview: true,
    });
    setTimeout(
      () =>
        dispatch({
          type: ACTIONS.ADD_REVIEW,
          review,
        }),
      1000,
    );
  };

  const handleBiGenericClick = useCallback(
    async ({
      clickType,
      clickData,
    }: {
      clickType: string;
      clickData?: any;
    }) => {
      logBiClick({ clickType, click_data: clickData });
    },
    [logBiClick],
  );

  const deleteReply = useCallback(
    async (id: string) => {
      const deleteReplyResponse = await httpClient.post<any>(
        TEMPLATE_DELETE_REVIEW_REPLY_API_URL,
        { reviewId: id },
      );
      dispatch({
        type: ACTIONS.UPDATE_REVIEW,
        payload: deleteReplyResponse.data,
      });
    },
    [httpClient],
  );

  const writeReplyToReview = useCallback(
    async (id: string, replyBody: string) => {
      {
        const writeReplyReviewResponse = await httpClient.post<any>(
          TEMPLATE_POST_REVIEW_REPLY_API_URL,
          {
            reply: replyBody,
            reviewId: id,
          },
        );
        const updatedReview = writeReplyReviewResponse.data;
        dispatch({
          type: ACTIONS.UPDATE_REVIEW,
          payload: updatedReview,
        });
        await handleBiGenericClick({
          clickType: 'Submit Reply',
          clickData: { replay: replyBody },
        });
      }
    },
    [dispatch, httpClient],
  );

  const onEdit = () => {
    dispatchModal({
      type: MODAL_ACTIONS.SHOW_REVIEW_MODAL,
      showReviewModal: true,
    });
  };

  return (
    <Box dataHook="template-reviews" width="100%">
      <DynamicTemplateReviewModal
        id={template.id}
        templateName={template.title}
        businessName={template.partnerProfileDetails.businessName}
        templateMediaSources={template.media.additionalImages}
        onSubmitSuccess={onSubmitSuccess}
        isMobileView={isMobileView}
        onClose={() => {
          dispatch({
            type: ACTIONS.REVIEW_MODAL_AUTO_FOCUS,
            reviewModalAutoFocus: false,
          });
        }}
        handleBiGenericClick={handleBiGenericClick}
      />
      <Reviews
        handleBiGenericClick={handleBiGenericClick}
        showReviewsCount={showReviewsCount}
        i18nKeys={translations}
        fetchMoreOnScroll={false}
        isMobileView={isMobileView}
        isMyProfile={template.isOwnerView}
        averageReviewRating={reviewsInfo.average}
        reviews={reviewsInfo.reviews}
        cursor={undefined}
        reviewsCount={reviewsInfo.total}
        fetchNextReviews={async () => {}}
        marginBottom={isMobileView ? 'SP4' : 'SP1'}
        loadNewReview={loadNewReview}
        onEdit={onEdit}
        userReview={userReviewForTemplate}
        addNewReviewBanner={
          !loadNewReview &&
          showReviewBanner && (
            <AddReviewBanner
              openReviewModal={openReviewModal}
              handleBiGenericClick={handleBiGenericClick}
            />
          )
        }
        replyProps={{
          hasActionMenu: true,
          isUsingReplyModal: true,
          writeReplyToReview,
          deleteReply,
        }}
      />
    </Box>
  );
};

export default TemplateReviews;
